import React from 'react';
import { useTranslation } from 'react-i18next';

import { localizeLogoLabel } from '../casino-settings/components/LogosSection/LogosSection';
import type { DisplayLogo, ResolvedLogos } from '../casino-settings/logos';
import { buildZip, dataUriToBytes, type ZipEntry } from '../casino-settings/zip';

/** Filesystem-safe slug for the zip filename. */
const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'casino';

/** Fire a browser download for a URL (object URL or data URI). */
const anchorDownload = (href: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export type LogoDownloadsArgs = {
  logoSet: ResolvedLogos;
  /** Names the zip (`<brand>-logos.zip`). */
  brandName: string;
  /** The caller's toast; the copy arrives already through `t`. */
  notify: (message: string, placeholders?: Record<string, string | number>) => void;
};

export type LogoDownloads = {
  /** Download one logo, and say so. */
  downloadLogo: (logo: DisplayLogo) => void;
  /** Download every logo in the set as one zip, and say how many it holds. */
  downloadAll: () => void;
};

/**
 * The logo downloads, shared by the Logos page and the Website hub's Logos card so both build the same files and say
 * the same thing: one logo at a time, or the whole set as one zip.
 */
export const useLogoDownloads = ({ logoSet, brandName, notify }: LogoDownloadsArgs): LogoDownloads => {
  const { t } = useTranslation();

  // Download a single logo. Schematic fallbacks are `data:` URIs (download straight); real assets are
  // remote, so fetch → blob first, and if that's blocked (CORS) open the asset so the owner can still save it.
  const downloadLogo = React.useCallback(async (logo: DisplayLogo): Promise<void> => {
    if (logo.isData) {
      anchorDownload(logo.src, logo.filename);
      return;
    }
    try {
      const response = await fetch(logo.src, { mode: 'cors' });
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      anchorDownload(url, logo.filename);
      window.setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      // Fallback when the CORS fetch is blocked: open the asset so the owner can still save it, but only if
      // it's an http(s) URL. logo.src is backend-set brand config; a javascript:/data: value would otherwise
      // run script in the studio origin via window.open (noopener guards the window handle, not script-scheme
      // execution).
      try {
        const { protocol } = new URL(logo.src);
        if (protocol === 'http:' || protocol === 'https:') {
          window.open(logo.src, '_blank', 'noopener');
        }
      } catch {
        // Unparseable URL: do nothing.
      }
    }
  }, []);

  const onDownloadLogo = React.useCallback(
    (logo: DisplayLogo) => {
      void downloadLogo(logo);
      notify(t('{{label}} downloaded'), { label: localizeLogoLabel(logo.label, t) });
    },
    [downloadLogo, notify, t],
  );

  // "Download all" bundles every logo into ONE zip (a single download, no multi-file browser prompt): grab
  // each asset's bytes (decode the schematic `data:` URIs, fetch the remote ones), then build + download the
  // archive.
  const onDownloadAllLogos = React.useCallback(() => {
    const all = [logoSet.primary, ...logoSet.variants];
    void (async () => {
      const fetched = await Promise.all(
        all.map(async (logo): Promise<ZipEntry | null> => {
          try {
            const bytes = logo.isData
              ? dataUriToBytes(logo.src)
              : new Uint8Array(await (await fetch(logo.src, { mode: 'cors' })).arrayBuffer());
            return { name: logo.filename, data: bytes };
          } catch {
            return null;
          }
        }),
      );
      const entries = fetched.filter((entry): entry is ZipEntry => entry !== null);
      if (entries.length === 0) {
        notify(t("Couldn't prepare the logo archive"));
        return;
      }
      const url = URL.createObjectURL(buildZip(entries));
      anchorDownload(url, `${slug(brandName)}-logos.zip`);
      window.setTimeout(() => URL.revokeObjectURL(url), 10000);
      notify(t('Downloaded {{count}} logos as a zip'), { count: entries.length });
    })();
  }, [logoSet, brandName, notify, t]);

  return { downloadLogo: onDownloadLogo, downloadAll: onDownloadAllLogos };
};
